import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts';
import { NotificationTypeMetrics } from '../../../types/notification-admin';

interface NotificationTypeBreakdownProps {
  data: NotificationTypeMetrics[];
  loading?: boolean;
  showTable?: boolean;
}

/**
 * NotificationTypeBreakdown Component
 * Displays notification distribution by type with pie chart and optional table
 */
export const NotificationTypeBreakdown: React.FC<NotificationTypeBreakdownProps> = ({
  data,
  loading = false,
  showTable = false,
}) => {
  // Colors for pie chart
  const COLORS = [
    '#0088FE',
    '#00C49F',
    '#FFBB28',
    '#FF8042',
    '#8884D8',
    '#82CA9D',
    '#FFC658',
    '#FF7C7C',
    '#A4DE6C',
    '#D0ED57',
  ];

  // Transform data for pie chart (top 10 types)
  const pieData = data.slice(0, 10).map((item) => ({
    name: item.typeLabel,
    value: item.totalSent,
  }));

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Notification Types
          </Typography>
          <Skeleton variant="circular" width={300} height={300} sx={{ mx: 'auto' }} />
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Notification Types
          </Typography>
          <Box
            sx={{
              height: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No notifications sent yet
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Notification Types
        </Typography>

        {/* Pie Chart */}
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({name, percent}: any) => `${name}: ${((percent as number) * 100).toFixed(0)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>

        {/* Optional Table */}
        {showTable && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Detailed Breakdown
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Sent</TableCell>
                    <TableCell align="right">Read Rate</TableCell>
                    <TableCell align="right">Action Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.slice(0, 10).map((row) => (
                    <TableRow key={row.type}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: COLORS[data.indexOf(row) % COLORS.length],
                            }}
                          />
                          <Typography variant="body2">{row.typeLabel}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {row.totalSent.toLocaleString()}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={`${row.readRate.toFixed(1)}%`}
                          size="small"
                          color={row.readRate >= 70 ? 'success' : row.readRate >= 40 ? 'warning' : 'error'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {row.totalActioned > 0 ? (
                          <Chip
                            label={`${row.actionRate.toFixed(1)}%`}
                            size="small"
                            color={row.actionRate >= 30 ? 'success' : 'default'}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
